import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { ContactDataService } from '@app/services/contact-data.service';
import {
  CONTACT_INQUIRY_STATUS_CLASSES,
  CONTACT_INQUIRY_STATUS_LABELS,
  ContactInquiryStatus,
  MyInquiry
} from '@app/model/contact.model';

/** Status filter options shown in the dropdown. */
interface StatusFilterOption {
  value: ContactInquiryStatus | '';
  labelKey: string;
}

@Component({
  selector: 'meu-app-my-inquiries',
  templateUrl: './app-my-inquiries.component.html',
  styleUrls: ['./app-my-inquiries.component.scss']
})
export class AppMyInquiriesComponent implements OnInit, OnDestroy {
  /** List of inquiries returned by the API. */
  inquiries: MyInquiry[] = [];
  total = 0;
  pagesize = 10;
  pageIndex = 1;
  loading = false;

  /** Free-text search keyword (subject / message / department / person). */
  searchTerm = '';
  private searchSubject = new Subject<string>();

  /** Currently selected status filter ('' = all). */
  statusFilter: ContactInquiryStatus | '' = '';

  /** Static status metadata (labels & badge classes). */
  statusLabels = CONTACT_INQUIRY_STATUS_LABELS;
  statusClasses = CONTACT_INQUIRY_STATUS_CLASSES;

  /** Status filter dropdown options. */
  statusOptions: StatusFilterOption[] = [
    { value: '', labelKey: 'MY_INQUIRIES.STATUS_ALL' },
    { value: 'new', labelKey: 'MY_INQUIRIES.STATUS_NEW' },
    { value: 'in_progress', labelKey: 'MY_INQUIRIES.STATUS_IN_PROGRESS' },
    { value: 'resolved', labelKey: 'MY_INQUIRIES.STATUS_RESOLVED' },
    { value: 'closed', labelKey: 'MY_INQUIRIES.STATUS_CLOSED' }
  ];

  /** Id of the inquiry whose detail panel is expanded. */
  expandedId: string | null = null;

  /** Detail currently being loaded / shown in the expanded panel. */
  detailLoading = false;
  detail: MyInquiry | null = null;
  detailError: string | null = null;

  /** Cached details keyed by inquiry id to avoid refetching. */
  private detailCache: { [id: string]: MyInquiry } = {};

  private destroy$ = new Subject<void>();

  constructor(
    private contactDataService: ContactDataService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    // Debounced free-text search.
    this.searchSubject
      .pipe(debounceTime(350), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageIndex = 1;
        this.loadInquiries();
      });

    // Initial load.
    this.loadInquiries();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject.complete();
  }

  // ---------------------- Data loading ----------------------

  loadInquiries(): void {
    this.loading = true;
    this.expandedId = null;
    this.detail = null;
    this.detailError = null;

    const filters = this.buildFilters();
    this.contactDataService
      .getMyInquiries({
        filters,
        page: this.pageIndex,
        pageSize: this.pagesize,
        sorts: '-created_at'
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.inquiries = result.collection || [];
          this.total = result.total || 0;
          this.pagesize = result.pagesize || this.pagesize;
          this.pageIndex = result.pageIndex || this.pageIndex;
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.inquiries = [];
          this.total = 0;
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
  }

  /** Build the Sieve filters string combining status + multi-field keyword. */
  private buildFilters(): string {
    const conditions: string[] = [];
    if (this.statusFilter) {
      conditions.push(`status==${this.statusFilter}`);
    }
    const keyword = (this.searchTerm || '').trim();
    if (keyword) {
      // Multi-field search across subject/message/department/person.
      conditions.push(`subject|message|departmentName|personName@=${keyword}`);
    }
    return conditions.join(',');
  }

  onSearchChange(value: string): void {
    this.searchTerm = value;
    this.searchSubject.next(value);
  }

  onStatusChange(): void {
    this.pageIndex = 1;
    this.loadInquiries();
  }

  // ---------------------- Pagination ----------------------

  paginatorChange(event: any): void {
    this.pagesize = event.pageSize;
    this.pageIndex = event.pageIndex + 1;
    this.loadInquiries();
  }

  // ---------------------- Detail expansion ----------------------

  toggleDetail(inquiry: MyInquiry): void {
    if (this.expandedId === inquiry.id) {
      this.expandedId = null;
      this.detail = null;
      this.detailError = null;
      return;
    }

    this.expandedId = inquiry.id;
    this.detailError = null;

    // Use cached detail if available, otherwise fetch from the API.
    if (this.detailCache[inquiry.id]) {
      this.detail = this.detailCache[inquiry.id];
      return;
    }

    // The list item already contains all fields; use it as the detail
    // and additionally call the detail endpoint to refresh admin_notes.
    this.detail = inquiry;
    this.detailLoading = true;
    this.contactDataService
      .getMyInquiryDetail(inquiry.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.detailLoading = false;
          if (data) {
            this.detailCache[inquiry.id] = data;
            this.detail = data;
            // Keep the list item in sync with the freshest detail.
            const idx = this.inquiries.findIndex((i) => i.id === data.id);
            if (idx >= 0) {
              this.inquiries[idx] = data;
            }
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.detailLoading = false;
          const msg =
            (err && err.error && err.error.message) ||
            this.translate.instant('MY_INQUIRIES.ERR_DETAIL');
          this.detailError = msg;
          this.cdr.detectChanges();
        }
      });
  }

  // ---------------------- Display helpers ----------------------

  /** Format an ISO date string to a localized DD/MM/YYYY HH:mm display. */
  formatDate(value: string | null | undefined): string {
    if (!value) {
      return '—';
    }
    const d = new Date(value);
    if (isNaN(d.getTime())) {
      return value;
    }
    const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  /** Returns the translated label for a given inquiry status. */
  statusLabel(status: ContactInquiryStatus): string {
    // Prefer the i18n key, fall back to the static Vietnamese label map.
    const key = `MY_INQUIRIES.STATUS_${this.camelToSnake(status).toUpperCase()}`;
    const translated = this.translate.instant(key);
    if (translated && translated !== key) {
      return translated;
    }
    return this.statusLabels[status] || status;
  }

  statusClass(status: ContactInquiryStatus): string {
    return this.statusClasses[status] || '';
  }

  private camelToSnake(value: string): string {
    return value.replace(/([A-Z])/g, '_$1').toLowerCase();
  }
}
